import { useRecoilCallback } from 'recoil';
import type { ToolInteractionUpdateViewportPresentationStateResponse } from 'generated/graphql';
import { useStudies } from '../../../../Viewer/StudyLoader/useStudies';
import {
  getViewportConfigurationSelector,
  viewportDisplayConfigurationSelector,
} from '../../../../ViewportsConfigurations/state';
import {
  windowLevelDisplaySelector,
  DEFAULT_WINDOW_LEVEL_STATE,
} from '../../../../ViewportsConfigurations/windowLevelState';
import { useViewportId } from '../../../modules/state';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { useInitCamera } from '../../../modules/useInitCamera';
import { useInitWindowLevel } from '../../../modules/useWindowLevel';
import { WithApolloContext } from 'modules/Apollo/types';

export function useUpdateViewportPresentationStateResponse({
  context,
}: WithApolloContext): (
  response: Readonly<ToolInteractionUpdateViewportPresentationStateResponse>
) => void {
  const activeViewportId = useViewportId();
  const { groupedStudies } = useStudies({ context });
  const currentCaseId = useCurrentCaseId();
  const initCamera = useInitCamera(activeViewportId);
  const initWindowLevel = useInitWindowLevel(activeViewportId);

  const updateViewportConfigurationParams2d = useRecoilCallback(
    ({ snapshot, set }) =>
      (response: any) => {
        const viewportId = response.viewportId ?? activeViewportId;
        const viewportConfiguration = snapshot
          .getLoadable(
            getViewportConfigurationSelector({
              viewportId,
              currentCaseId,
              groupedStudies,
            })
          )
          .valueMaybe();

        if (viewportConfiguration == null) {
          return;
        }

        const displayConfigKey = {
          stackSmid: viewportConfiguration?.stack?.smid,
          viewType: viewportConfiguration?.viewType,
          viewportId: response.viewportId,
        };

        const selector = viewportDisplayConfigurationSelector(displayConfigKey);

        set(selector, (config) => {
          return {
            ...config,
            params2d: {
              ...config?.params2d,
              rotate: response.rotate != null ? response.rotate : 0,
              zoom: response.zoom ?? config?.params2d?.zoom,
            },
          };
        });

        if (response.window == null || response.level == null) {
          setTimeout(() => {
            initCamera();
            initWindowLevel();
          }, 500);
          return;
        }
        // Write window/level to the dedicated state

        const wlSelector = windowLevelDisplaySelector({
          seriesSmid: viewportConfiguration?.series?.smid,
          viewType: viewportConfiguration?.viewType,
          viewportId: response.viewportId,
        });
        set(wlSelector, (currentWL) => {
          const base = currentWL ?? DEFAULT_WINDOW_LEVEL_STATE;
          return {
            ...base,
            window: response.window ?? base.window,
            level: response.level ?? base.level,
          };
        });

        setTimeout(() => {
          initCamera();
          initWindowLevel();
        }, 500);
      }
  );

  return updateViewportConfigurationParams2d;
}
